import type { OcrLine, Rect } from "@aimosaic/core";

export interface OcrDiagnostics {
  rawTextLength: number;
  lineCount: number;
  wordCount: number;
  enhancedLineCount?: number;
  enhancedWordCount?: number;
  cropLineCount?: number;
  cropWordCount?: number;
  enhancedSkipped?: boolean;
  cacheHit?: boolean;
  elapsedMs?: number;
  usedFallback: boolean;
  errorCode?: "ocr-failed" | "no-text";
}

export interface OcrRecognition {
  lines: OcrLine[];
  rawText: string;
  diagnostics: OcrDiagnostics;
}

interface TesseractBbox {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

interface TesseractLine {
  text?: string;
  bbox?: TesseractBbox;
  words?: TesseractWord[];
}

interface TesseractParagraph {
  lines?: TesseractLine[];
}

interface TesseractBlock {
  bbox?: TesseractBbox;
  text?: string;
  lines?: TesseractLine[];
  paragraphs?: TesseractParagraph[];
}

interface TesseractData {
  text?: string;
  blocks?: TesseractBlock[] | null;
  tsv?: string | null;
}

interface TesseractWord {
  text?: string;
  bbox?: TesseractBbox;
  confidence?: number;
}

export interface OcrImagePixels {
  width: number;
  height: number;
  data: Uint8ClampedArray;
}

export interface OcrPreprocessOptions {
  scale?: number;
  mode?: "enhanced" | "threshold";
  invertDark?: boolean;
}

export interface OcrImageVariant {
  id: string;
  scale: number;
  offsetX: number;
  offsetY: number;
  width: number;
  height: number;
}

interface RuntimeOcrImageVariant extends OcrImageVariant {
  dataUrl: string;
}

const OCR_CACHE_LIMIT = 4;
const DEFAULT_OCR_RECOGNIZE_TIMEOUT_MS = 25000;
const ocrCache = new Map<string, OcrRecognition>();
let ocrRecognizeTimeoutMs = DEFAULT_OCR_RECOGNIZE_TIMEOUT_MS;

interface TesseractWorker {
  setParameters?(params: Record<string, string>): Promise<unknown>;
  recognize(
    image: string,
    options?: Record<string, unknown>,
    output?: Record<string, boolean>
  ): Promise<{ data: TesseractData }>;
  terminate(): Promise<unknown>;
}

type CreateWorker = (
  langs?: string | string[],
  oem?: number,
  options?: Record<string, unknown>
) => Promise<TesseractWorker>;

export function tesseractWorkerOptions(baseUrl: string = import.meta.env.BASE_URL): Record<string, unknown> {
  const base = normalizePublicBaseUrl(baseUrl);
  return {
    workerPath: `${base}vendor/tesseract/worker.min.js`,
    corePath: `${base}vendor/tesseract/core`,
    langPath: `${base}vendor/tesseract/lang`,
    workerBlobURL: false
  };
}

function normalizePublicBaseUrl(baseUrl: string): string {
  const base = baseUrl.trim() || "/";
  return base.endsWith("/") ? base : `${base}/`;
}

export async function recognizeOcrLines(imageDataUrl: string, width: number, height: number): Promise<OcrRecognition> {
  return withOcrCache(`regular:${ocrCacheKey(imageDataUrl, width, height)}`, () =>
    runOcr(imageDataUrl, width, height, ["eng", "kor"])
  );
}

export async function recognizeUrlOcrLines(
  imageDataUrl: string,
  width: number,
  height: number,
  seedLines: OcrLine[] = []
): Promise<OcrRecognition> {
  return withOcrCache(`url:auto:${ocrCacheKey(imageDataUrl, width, height)}`, () =>
    recognizeUrlOcrLinesUncached(imageDataUrl, width, height, seedLines)
  );
}

async function recognizeUrlOcrLinesUncached(
  imageDataUrl: string,
  width: number,
  height: number,
  seedLines: OcrLine[]
): Promise<OcrRecognition> {
  const sourceVariant: RuntimeOcrImageVariant = {
    id: "source",
    dataUrl: imageDataUrl,
    scale: 1,
    offsetX: 0,
    offsetY: 0,
    width,
    height
  };
  const tesseractModule = await import("tesseract.js");
  const moduleDefault = tesseractModule.default as unknown as { createWorker?: CreateWorker } | undefined;
  const createWorker = (tesseractModule.createWorker ?? moduleDefault?.createWorker) as unknown as
    | CreateWorker
    | undefined;
  if (!createWorker) {
    return {
      lines: [],
      rawText: "",
      diagnostics: { rawTextLength: 0, lineCount: 0, wordCount: 0, usedFallback: false, errorCode: "ocr-failed" }
    };
  }

  let worker: TesseractWorker | null = null;
  try {
    worker = await createWorker("eng", 1, tesseractWorkerOptions());
    await worker.setParameters?.(urlOcrParameters("11"));
    const source = await recognizeVariantsWithWorker(worker, [sourceVariant]);
    const fastLines = mergeOcrLines(seedLines, source.lines);
    const shouldEnhance = shouldRunEnhancedUrlOcr(fastLines);
    if (!shouldEnhance) {
      return withUrlDiagnostics(source, emptyOcrRecognition(), emptyOcrRecognition(), true);
    }

    await worker.setParameters?.(urlOcrParameters("6"));
    const enhancedVariant = await createFullImageOcrVariant(imageDataUrl, width, height).catch(() => null);
    const enhanced = enhancedVariant
      ? await recognizeVariantsWithWorker(worker, [enhancedVariant])
      : emptyOcrRecognition();
    const cropVariants = await createRowCropOcrVariants(
      imageDataUrl,
      width,
      height,
      mergeOcrLines(fastLines, enhanced.lines)
    ).catch(() => []);
    const crop = cropVariants.length > 0
      ? await recognizeVariantsWithWorker(worker, cropVariants)
      : emptyOcrRecognition();
    return withUrlDiagnostics(source, enhanced, crop, false);
  } catch {
    return {
      lines: [],
      rawText: "",
      diagnostics: { rawTextLength: 0, lineCount: 0, wordCount: 0, usedFallback: false, errorCode: "ocr-failed" }
    };
  } finally {
    await worker?.terminate().catch(() => undefined);
  }
}

function withUrlDiagnostics(
  source: OcrRecognition,
  enhanced: OcrRecognition,
  crop: OcrRecognition,
  enhancedSkipped: boolean
): OcrRecognition {
  const lines = mergeOcrLines(mergeOcrLines(source.lines, enhanced.lines), crop.lines);
  const rawText = [source.rawText, enhanced.rawText, crop.rawText].filter(Boolean).join("\n");
  return {
    lines,
    rawText,
    diagnostics: {
      rawTextLength: rawText.length,
      lineCount: lines.length,
      wordCount: countWords(lines),
      enhancedLineCount: enhanced.diagnostics.lineCount,
      enhancedWordCount: enhanced.diagnostics.wordCount,
      cropLineCount: crop.diagnostics.lineCount,
      cropWordCount: crop.diagnostics.wordCount,
      enhancedSkipped,
      usedFallback:
        source.diagnostics.usedFallback ||
        enhanced.diagnostics.usedFallback ||
        crop.diagnostics.usedFallback,
      ...(lines.length === 0 ? { errorCode: source.diagnostics.errorCode ?? enhanced.diagnostics.errorCode ?? crop.diagnostics.errorCode } : {})
    }
  };
}

export function shouldRunEnhancedUrlOcr(lines: OcrLine[]): boolean {
  const urlishRows = lines.filter((line) => hasUrlOcrRowContext(line.text));
  if (urlishRows.length === 0) return false;
  const candidateCount = countFastUrlCandidates(lines);
  if (urlishRows.length >= 3) {
    return candidateCount < urlishRows.length * 2;
  }
  return candidateCount < 2;
}

function hasUrlOcrRowContext(text: string): boolean {
  return /wildcard|DSM|DDNS|https?:\/\/|Local|DNS|업데이트|활성|[A-Za-z0-9*]+\s*[.@]\s*[A-Za-z0-9]/i.test(text);
}

function countFastUrlCandidates(lines: OcrLine[]): number {
  const expression =
    /https?:\/\/[^\s"'<>]+|\b(?:(?:25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|1?\d?\d)\b|\b(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+(?:com|dev|org|kr|net|io)\b/gi;
  return lines.reduce((count, line) => count + [...line.text.matchAll(expression)].length, 0);
}

export function clearOcrCacheForTests(): void {
  ocrCache.clear();
}

export function setOcrRecognizeTimeoutForTests(timeoutMs: number | null): void {
  ocrRecognizeTimeoutMs = timeoutMs ?? DEFAULT_OCR_RECOGNIZE_TIMEOUT_MS;
}

async function withOcrCache(key: string, producer: () => Promise<OcrRecognition>): Promise<OcrRecognition> {
  const cached = ocrCache.get(key);
  if (cached) {
    return {
      ...cached,
      diagnostics: {
        ...cached.diagnostics,
        cacheHit: true,
        elapsedMs: 0
      }
    };
  }

  const startedAt = nowMs();
  const result = await producer();
  const next: OcrRecognition = {
    ...result,
    diagnostics: {
      ...result.diagnostics,
      cacheHit: false,
      elapsedMs: Math.round(nowMs() - startedAt)
    }
  };
  rememberOcrResult(key, next);
  return next;
}

function rememberOcrResult(key: string, result: OcrRecognition): void {
  ocrCache.set(key, result);
  while (ocrCache.size > OCR_CACHE_LIMIT) {
    const oldestKey = ocrCache.keys().next().value;
    if (!oldestKey) break;
    ocrCache.delete(oldestKey);
  }
}

function ocrCacheKey(imageDataUrl: string, width: number, height: number): string {
  return `${width}x${height}:${imageDataUrl.length}:${hashString(imageDataUrl)}`;
}

function hashString(value: string): string {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

function nowMs(): number {
  return typeof performance === "undefined" ? Date.now() : performance.now();
}

async function runOcr(
  imageDataUrl: string,
  width: number,
  height: number,
  languages: string | string[],
  parameters: Record<string, string> = {}
): Promise<OcrRecognition> {
  return runOcrVariants(
    [{ id: "source", dataUrl: imageDataUrl, scale: 1, offsetX: 0, offsetY: 0, width, height }],
    languages,
    parameters
  );
}

async function runOcrVariants(
  variants: RuntimeOcrImageVariant[],
  languages: string | string[],
  parameters: Record<string, string> = {}
): Promise<OcrRecognition> {
  if (variants.length === 0) return emptyOcrRecognition();
  const tesseractModule = await import("tesseract.js");
  const moduleDefault = tesseractModule.default as unknown as { createWorker?: CreateWorker } | undefined;
  const createWorker = (tesseractModule.createWorker ?? moduleDefault?.createWorker) as unknown as
    | CreateWorker
    | undefined;
  if (!createWorker) {
    return {
      lines: [],
      rawText: "",
      diagnostics: { rawTextLength: 0, lineCount: 0, wordCount: 0, usedFallback: false, errorCode: "ocr-failed" }
    };
  }

  let worker: TesseractWorker | null = null;
  try {
    worker = await createWorker(languages, 1, tesseractWorkerOptions());
    if (Object.keys(parameters).length > 0) {
      await worker.setParameters?.(parameters);
    }
    return await recognizeVariantsWithWorker(worker, variants);
  } catch {
    return {
      lines: [],
      rawText: "",
      diagnostics: { rawTextLength: 0, lineCount: 0, wordCount: 0, usedFallback: false, errorCode: "ocr-failed" }
    };
  } finally {
    await worker?.terminate().catch(() => undefined);
  }
}

async function recognizeVariantsWithWorker(
  worker: TesseractWorker,
  variants: RuntimeOcrImageVariant[]
): Promise<OcrRecognition> {
  if (variants.length === 0) return emptyOcrRecognition();
  const lines: OcrLine[] = [];
  const rawTexts: string[] = [];
  let usedFallback = false;
  let errorCode: OcrDiagnostics["errorCode"];

  for (const variant of variants) {
    const result = await withOcrTimeout(
      worker.recognize(variant.dataUrl, {}, { blocks: true, tsv: true })
    );
    const extracted = extractOcrLinesFromData(result.data, variant.width, variant.height);
    rawTexts.push(extracted.rawText);
    usedFallback ||= extracted.diagnostics.usedFallback;
    errorCode ??= extracted.diagnostics.errorCode;
    lines.push(...mapOcrLinesFromVariant(extracted.lines, variant));
  }

  const merged = dedupeOcrLines(lines);
  const rawText = rawTexts.filter(Boolean).join("\n");
  return {
    lines: merged,
    rawText,
    diagnostics: {
      rawTextLength: rawText.length,
      lineCount: merged.length,
      wordCount: countWords(merged),
      usedFallback,
      ...(merged.length === 0 && errorCode ? { errorCode } : {})
    }
  };
}

function withOcrTimeout<T>(promise: Promise<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => reject(new Error("ocr-timeout")), ocrRecognizeTimeoutMs);
    promise.then(
      (value) => {
        clearTimeout(timeoutId);
        resolve(value);
      },
      (error) => {
        clearTimeout(timeoutId);
        reject(error);
      }
    );
  });
}

function urlOcrParameters(psm: "6" | "11"): Record<string, string> {
  return {
    tessedit_pageseg_mode: psm,
    preserve_interword_spaces: "1",
    tessedit_char_whitelist:
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789:/.-_?=&%#@*+~|[](){}"
  };
}

function emptyOcrRecognition(): OcrRecognition {
  return {
    lines: [],
    rawText: "",
    diagnostics: { rawTextLength: 0, lineCount: 0, wordCount: 0, usedFallback: false }
  };
}

export function preprocessOcrImageData(
  source: OcrImagePixels,
  options: OcrPreprocessOptions = {}
): OcrImagePixels {
  const scale = Math.max(1, options.scale ?? 2);
  const mode = options.mode ?? "enhanced";
  const outWidth = Math.max(1, Math.round(source.width * scale));
  const outHeight = Math.max(1, Math.round(source.height * scale));
  const average = averageLuminance(source);
  const shouldInvert = Boolean(options.invertDark ?? true) && average < 136;
  const gray = new Uint8ClampedArray(outWidth * outHeight);

  for (let y = 0; y < outHeight; y += 1) {
    const sourceY = Math.min(source.height - 1, Math.floor(y / scale));
    for (let x = 0; x < outWidth; x += 1) {
      const sourceX = Math.min(source.width - 1, Math.floor(x / scale));
      const sourceIndex = (sourceY * source.width + sourceX) * 4;
      let value = luminance(
        source.data[sourceIndex],
        source.data[sourceIndex + 1],
        source.data[sourceIndex + 2]
      );
      value = contrast(value, mode === "threshold" ? 1.7 : 1.45);
      if (shouldInvert) value = 255 - value;
      gray[y * outWidth + x] = value;
    }
  }

  const sharpened = sharpenGray(gray, outWidth, outHeight);
  const finalGray =
    mode === "threshold" ? adaptiveThreshold(sharpened, outWidth, outHeight) : sharpened;
  const data = new Uint8ClampedArray(outWidth * outHeight * 4);
  for (let index = 0; index < finalGray.length; index += 1) {
    const value = finalGray[index];
    const offset = index * 4;
    data[offset] = value;
    data[offset + 1] = value;
    data[offset + 2] = value;
    data[offset + 3] = 255;
  }

  return { width: outWidth, height: outHeight, data };
}

export function mapOcrLinesFromVariant(lines: OcrLine[], variant: OcrImageVariant): OcrLine[] {
  return lines.map((line) => ({
    ...line,
    bbox: mapRectFromVariant(line.bbox, variant),
    ...(line.words
      ? { words: line.words.map((word) => ({ ...word, bbox: mapRectFromVariant(word.bbox, variant) })) }
      : {})
  }));
}

async function createFullImageOcrVariant(
  imageDataUrl: string,
  width: number,
  height: number
): Promise<RuntimeOcrImageVariant | null> {
  if (typeof document === "undefined") return null;
  const image = await loadImage(imageDataUrl);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) return null;
  context.drawImage(image, 0, 0, width, height);
  const pixels = preprocessOcrImageData(context.getImageData(0, 0, width, height), {
    scale: ocrScaleForImage(width, height),
    mode: "enhanced",
    invertDark: true
  });

  return {
    id: "enhanced",
    dataUrl: pixelsToDataUrl(pixels),
    scale: pixels.width / width,
    offsetX: 0,
    offsetY: 0,
    width: pixels.width,
    height: pixels.height
  };
}

async function createRowCropOcrVariants(
  imageDataUrl: string,
  width: number,
  height: number,
  lines: OcrLine[]
): Promise<RuntimeOcrImageVariant[]> {
  if (typeof document === "undefined") return [];
  const rows = selectUrlRowRects(lines, width, height);
  if (rows.length === 0) return [];
  const image = await loadImage(imageDataUrl);
  const variants: RuntimeOcrImageVariant[] = [];

  for (const [index, row] of rows.entries()) {
    const scale = 2.4;
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(row.width * scale));
    canvas.height = Math.max(1, Math.round(row.height * scale));
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) continue;
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";
    context.drawImage(
      image,
      row.x,
      row.y,
      row.width,
      row.height,
      0,
      0,
      canvas.width,
      canvas.height
    );
    const pixels = preprocessOcrImageData(context.getImageData(0, 0, canvas.width, canvas.height), {
      scale: 1,
      mode: "threshold",
      invertDark: true
    });
    variants.push({
      id: `row-${index}`,
      dataUrl: pixelsToDataUrl(pixels),
      scale,
      offsetX: row.x,
      offsetY: row.y,
      width: pixels.width,
      height: pixels.height
    });
  }

  return variants;
}

function selectUrlRowRects(lines: OcrLine[], imageWidth: number, imageHeight: number): Rect[] {
  const rows = lines
    .filter((line) => hasUrlOcrRowContext(line.text))
    .map((line) => {
      const rowHeight = clamp(line.bbox.height * 4.5, 70, 190);
      const centerY = line.bbox.y + line.bbox.height / 2;
      return clampRect(
        { x: 0, y: centerY - rowHeight / 2, width: imageWidth, height: rowHeight },
        imageWidth,
        imageHeight
      );
    })
    .filter((rect) => rect.width > 0 && rect.height > 0)
    .sort((a, b) => a.y - b.y);

  const merged: Rect[] = [];
  for (const row of rows) {
    const last = merged.at(-1);
    if (last && row.y <= last.y + last.height * 0.65) {
      const top = Math.min(last.y, row.y);
      const bottom = Math.max(last.y + last.height, row.y + row.height);
      last.y = top;
      last.height = bottom - top;
    } else {
      merged.push({ ...row });
    }
  }

  return merged.slice(0, 8);
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Image load failed"));
    image.src = dataUrl;
  });
}

function pixelsToDataUrl(pixels: OcrImagePixels): string {
  const canvas = document.createElement("canvas");
  canvas.width = pixels.width;
  canvas.height = pixels.height;
  const context = canvas.getContext("2d");
  if (!context) return "";
  context.putImageData(new ImageData(pixels.data as unknown as ImageDataArray, pixels.width, pixels.height), 0, 0);
  return canvas.toDataURL("image/png");
}

function ocrScaleForImage(width: number, height: number): number {
  const maxSide = Math.max(width, height);
  if (maxSide <= 0) return 2;
  return clamp(3600 / maxSide, 1.2, 2);
}

function averageLuminance(source: OcrImagePixels): number {
  let total = 0;
  const pixels = source.width * source.height;
  for (let index = 0; index < source.data.length; index += 4) {
    total += luminance(source.data[index], source.data[index + 1], source.data[index + 2]);
  }
  return total / Math.max(1, pixels);
}

function luminance(red: number, green: number, blue: number): number {
  return 0.299 * red + 0.587 * green + 0.114 * blue;
}

function contrast(value: number, factor: number): number {
  return clamp((value - 128) * factor + 128, 0, 255);
}

function sharpenGray(source: Uint8ClampedArray, width: number, height: number): Uint8ClampedArray {
  const out = new Uint8ClampedArray(source);
  if (width < 3 || height < 3) return out;
  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const index = y * width + x;
      const value =
        source[index] * 5 -
        source[index - 1] -
        source[index + 1] -
        source[index - width] -
        source[index + width];
      out[index] = clamp(value, 0, 255);
    }
  }
  return out;
}

function adaptiveThreshold(source: Uint8ClampedArray, width: number, height: number): Uint8ClampedArray {
  const radius = 8;
  const out = new Uint8ClampedArray(source.length);
  const integral = new Float64Array((width + 1) * (height + 1));
  for (let y = 1; y <= height; y += 1) {
    let row = 0;
    for (let x = 1; x <= width; x += 1) {
      row += source[(y - 1) * width + (x - 1)];
      integral[y * (width + 1) + x] = integral[(y - 1) * (width + 1) + x] + row;
    }
  }
  for (let y = 0; y < height; y += 1) {
    const top = Math.max(0, y - radius);
    const bottom = Math.min(height - 1, y + radius);
    for (let x = 0; x < width; x += 1) {
      const left = Math.max(0, x - radius);
      const right = Math.min(width - 1, x + radius);
      const area = (right - left + 1) * (bottom - top + 1);
      const sum = rectSum(integral, width, left, top, right, bottom);
      const localAverage = sum / area;
      out[y * width + x] = source[y * width + x] > localAverage - 8 ? 255 : 0;
    }
  }
  return out;
}

function rectSum(
  integral: Float64Array,
  width: number,
  left: number,
  top: number,
  right: number,
  bottom: number
): number {
  const stride = width + 1;
  const x1 = left;
  const y1 = top;
  const x2 = right + 1;
  const y2 = bottom + 1;
  return (
    integral[y2 * stride + x2] -
    integral[y1 * stride + x2] -
    integral[y2 * stride + x1] +
    integral[y1 * stride + x1]
  );
}

function mapRectFromVariant(rect: Rect, variant: OcrImageVariant): Rect {
  return {
    x: variant.offsetX + rect.x / variant.scale,
    y: variant.offsetY + rect.y / variant.scale,
    width: rect.width / variant.scale,
    height: rect.height / variant.scale
  };
}

function dedupeOcrLines(lines: OcrLine[]): OcrLine[] {
  return lines.reduce<OcrLine[]>((merged, line) => mergeOcrLines(merged, [line]), []);
}

function clampRect(rect: Rect, imageWidth: number, imageHeight: number): Rect {
  const x = clamp(rect.x, 0, imageWidth);
  const y = clamp(rect.y, 0, imageHeight);
  const right = clamp(rect.x + rect.width, x, imageWidth);
  const bottom = clamp(rect.y + rect.height, y, imageHeight);
  return { x, y, width: right - x, height: bottom - y };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function mergeOcrLines(primary: OcrLine[], secondary: OcrLine[]): OcrLine[] {
  const merged: OcrLine[] = [...primary];
  for (const line of secondary) {
    const duplicate = merged.some(
      (candidate) => normalizeLineText(candidate.text) === normalizeLineText(line.text) && rectsOverlap(candidate.bbox, line.bbox)
    );
    if (!duplicate) {
      merged.push(line);
    }
  }
  return merged.sort((a, b) => a.bbox.y - b.bbox.y || a.bbox.x - b.bbox.x);
}

export function extractOcrLinesFromData(data: TesseractData, imageWidth: number, imageHeight: number): OcrRecognition {
  const rawText = data.text?.trim() ?? "";
  const lines = extractBlockLines(data.blocks ?? []);
  const tsvLines = extractTsvLines(data.tsv ?? "");
  const normalized = lines
    .map((line) => toOcrLine(line, matchingTsvWords(line, tsvLines)))
    .filter((line): line is OcrLine => Boolean(line));

  if (normalized.length > 0) {
    return {
      lines: normalized,
      rawText,
      diagnostics: {
        rawTextLength: rawText.length,
        lineCount: normalized.length,
        wordCount: countWords(normalized),
        usedFallback: false
      }
    };
  }

  if (tsvLines.length > 0) {
    return {
      lines: tsvLines,
      rawText,
      diagnostics: {
        rawTextLength: rawText.length,
        lineCount: tsvLines.length,
        wordCount: countWords(tsvLines),
        usedFallback: false
      }
    };
  }

  if (rawText) {
    return {
      lines: [{ text: rawText, bbox: { x: 0, y: 0, width: imageWidth, height: imageHeight } }],
      rawText,
      diagnostics: {
        rawTextLength: rawText.length,
        lineCount: 1,
        wordCount: 0,
        usedFallback: true
      }
    };
  }

  return {
    lines: [],
    rawText,
    diagnostics: {
      rawTextLength: 0,
      lineCount: 0,
      wordCount: 0,
      usedFallback: false,
      errorCode: "no-text"
    }
  };
}

function extractBlockLines(blocks: TesseractBlock[]): TesseractLine[] {
  return blocks.flatMap((block) => {
    if (block.paragraphs?.length) {
      return block.paragraphs.flatMap((paragraph) => paragraph.lines ?? []);
    }
    if (block.lines?.length) {
      return block.lines;
    }
    if (block.text && block.bbox) {
      return [{ text: block.text, bbox: block.bbox }];
    }
    return [];
  });
}

function toOcrLine(line: TesseractLine, fallbackWords: OcrLine["words"] = []): OcrLine | null {
  const text = line.text?.trim();
  if (!text || !line.bbox) return null;
  const words = (line.words ?? [])
    .map((word) => toOcrWord(word))
    .filter((word): word is NonNullable<ReturnType<typeof toOcrWord>> => Boolean(word));
  const nextWords = words.length > 0 ? words : fallbackWords;
  return {
    text,
    bbox: bboxToRect(line.bbox),
    ...(nextWords && nextWords.length > 0 ? { words: nextWords } : {})
  };
}

function matchingTsvWords(line: TesseractLine, tsvLines: OcrLine[]): OcrLine["words"] {
  if (!line.bbox || tsvLines.length === 0) return [];
  const rect = bboxToRect(line.bbox);
  const match = [...tsvLines]
    .map((tsvLine) => ({ line: tsvLine, score: lineMatchScore(rect, tsvLine.bbox) }))
    .sort((a, b) => b.score - a.score)[0];
  return match && match.score > 0 ? match.line.words : [];
}

function toOcrWord(word: TesseractWord) {
  const text = word.text?.trim();
  if (!text || !word.bbox) return null;
  return {
    text,
    bbox: bboxToRect(word.bbox),
    ...(typeof word.confidence === "number" ? { confidence: word.confidence } : {})
  };
}

function bboxToRect(bbox: TesseractBbox): Rect {
  return {
    x: bbox.x0,
    y: bbox.y0,
    width: Math.max(0, bbox.x1 - bbox.x0),
    height: Math.max(0, bbox.y1 - bbox.y0)
  };
}

function extractTsvLines(tsv: string): OcrLine[] {
  const rows = tsv.trim().split(/\r?\n/);
  if (rows.length < 2) return [];
  const headers = rows[0].split("\t");
  const groups = new Map<string, OcrLine["words"]>();

  for (const row of rows.slice(1)) {
    const cells = row.split("\t");
    const value = (name: string) => cells[headers.indexOf(name)] ?? "";
    if (value("level") !== "5") continue;

    const text = value("text").trim();
    if (!text) continue;

    const left = Number(value("left"));
    const top = Number(value("top"));
    const width = Number(value("width"));
    const height = Number(value("height"));
    if (![left, top, width, height].every(Number.isFinite)) continue;

    const confidence = Number(value("conf"));
    const key = [value("block_num"), value("par_num"), value("line_num")].join(":");
    const words = groups.get(key) ?? [];
    words.push({
      text,
      bbox: { x: left, y: top, width, height },
      ...(Number.isFinite(confidence) ? { confidence } : {})
    });
    groups.set(key, words);
  }

  return [...groups.values()].flatMap((words) => {
    if (!words?.length) return [];
    return [
      {
        text: words.map((word) => word.text).join(" "),
        bbox: unionRects(words.map((word) => word.bbox)),
        words
      }
    ];
  });
}

function countWords(lines: OcrLine[]): number {
  return lines.reduce((count, line) => count + (line.words?.length ?? 0), 0);
}

function unionRects(rects: Rect[]): Rect {
  const left = Math.min(...rects.map((rect) => rect.x));
  const top = Math.min(...rects.map((rect) => rect.y));
  const right = Math.max(...rects.map((rect) => rect.x + rect.width));
  const bottom = Math.max(...rects.map((rect) => rect.y + rect.height));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

function lineMatchScore(left: Rect, right: Rect): number {
  const verticalOverlap = Math.max(
    0,
    Math.min(left.y + left.height, right.y + right.height) - Math.max(left.y, right.y)
  );
  const horizontalOverlap = Math.max(
    0,
    Math.min(left.x + left.width, right.x + right.width) - Math.max(left.x, right.x)
  );
  const overlapScore =
    verticalOverlap / Math.max(1, Math.min(left.height, right.height)) +
    horizontalOverlap / Math.max(1, Math.min(left.width, right.width));
  const centerDistance = Math.abs(left.y + left.height / 2 - (right.y + right.height / 2));
  return overlapScore - centerDistance / 1000;
}

function normalizeLineText(text: string): string {
  return text.replace(/\s+/g, " ").trim().toLowerCase();
}

function rectsOverlap(left: Rect, right: Rect): boolean {
  const xOverlap = Math.max(
    0,
    Math.min(left.x + left.width, right.x + right.width) - Math.max(left.x, right.x)
  );
  const yOverlap = Math.max(
    0,
    Math.min(left.y + left.height, right.y + right.height) - Math.max(left.y, right.y)
  );
  const intersection = xOverlap * yOverlap;
  const smallerArea = Math.min(left.width * left.height, right.width * right.height);
  return smallerArea > 0 && intersection / smallerArea > 0.65;
}
